<template>
    <div>
        <b-modal
            :visible="value"
            id="report-request-modal"
            hide-footer
            @close="hide"
            @hidden="hidden"
        >
            <template #modal-title>
                {{ reportLabel || 'Run Report' }}
            </template>

            <div v-if="showGlobalNotice" class="mb-3 text-muted">
                Leave the filters blank or use the global option to run this report across all accessible records.
            </div>

            <div v-if="showUnitPrecedenceNotice" class="mb-3 text-muted">
                When a Unit ID is provided, the report uses that unit and ignores the selected publication.
            </div>

            <div v-if="visibleFields.publication_id" class="mb-3">
                <label for="modal-reports-publication-select" class="font-weight-bold">Publication SKU</label>
                <multiselect
                    id="modal-reports-publication-select"
                    v-model="selectedPublication"
                    :options="searchablePublicationOptions"
                    label="text"
                    track-by="value"
                    :placeholder="publicationPlaceholder"
                    :disabled="loadingPublications || publicationLocked"
                    :allow-empty="true"
                    :show-labels="false"
                />
            </div>

            <div
                v-if="
                    visibleFields.unit_id ||
                    visibleFields.week_id ||
                    visibleFields.article_id ||
                    visibleFields.related_media_id ||
                    visibleFields.related_media_ids
                "
                class="row"
            >
                <div v-if="visibleFields.unit_id" class="col-md-6 mb-3">
                    <label for="report-unit-id" class="font-weight-bold">Unit ID</label>
                    <b-form-input
                        id="report-unit-id"
                        v-model="form.unit_id"
                        type="number"
                        :disabled="unitLocked"
                    />
                </div>

                <div v-if="visibleFields.week_id" class="col-md-6 mb-3">
                    <label for="report-week-id" class="font-weight-bold">Week ID</label>
                    <b-form-input
                        id="report-week-id"
                        v-model="form.week_id"
                        type="number"
                        :disabled="weekLocked"
                    />
                </div>

                <div v-if="visibleFields.article_id" class="col-md-6 mb-3">
                    <label for="report-article-id" class="font-weight-bold">Article ID</label>
                    <b-form-input
                        id="report-article-id"
                        v-model="form.article_id"
                        type="number"
                    />
                </div>

                <div v-if="visibleFields.related_media_id" class="col-md-6 mb-3">
                    <label for="report-related-media-id" class="font-weight-bold">Related Media ID</label>
                    <b-form-input
                        id="report-related-media-id"
                        v-model="form.related_media_id"
                        type="number"
                    />
                </div>

                <div v-if="visibleFields.related_media_ids" class="col-md-12 mb-3">
                    <label for="report-related-media-ids" class="font-weight-bold">Related Media IDs</label>
                    <span class="d-block text-muted">Enter media IDs one per line</span>
                    <b-form-textarea
                        id="report-related-media-ids"
                        v-model="form.related_media_ids"
                        rows="4"
                        class="mt-2"
                    />
                </div>
            </div>

            <div v-if="reportType === 'related_media_videos_by_sku'" class="mb-3">
                <label for="report-media-type" class="font-weight-bold">Media Type</label>
                <b-form-select id="report-media-type" v-model="form.media_type">
                    <b-form-select-option :value="null">All types</b-form-select-option>
                    <b-form-select-option value="image">Image</b-form-select-option>
                    <b-form-select-option value="audio">Audio</b-form-select-option>
                    <b-form-select-option value="video">Video</b-form-select-option>
                </b-form-select>
            </div>

            <div v-if="visibleFields.global" class="mb-3">
                <b-form-checkbox v-model="form.global">
                    Run globally
                </b-form-checkbox>
            </div>

            <div class="mb-3">
                <label for="report-comment" class="font-weight-bold">Comment</label>
                <b-form-textarea
                    id="report-comment"
                    v-model="form.comment"
                    rows="3"
                    max-rows="6"
                />
            </div>

            <div class="d-flex justify-content-end mb-3">
                <b-button
                    variant="outline-secondary"
                    class="mr-2"
                    @click="hide"
                >
                    Cancel
                </b-button>
                <b-button
                    variant="primary"
                    :disabled="submitting"
                    @click="submit"
                >
                    <span v-if="submitting">Queueing...</span>
                    <span v-else>Queue Report</span>
                </b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    name: 'ReportRequestModal',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        reportType: {
            type: String,
            default: null,
        },
        reportLabel: {
            type: String,
            default: '',
        },
        publicationOptions: {
            type: Array,
            default: () => [],
        },
        loadingPublications: {
            type: Boolean,
            default: false,
        },
        submitting: {
            type: Boolean,
            default: false,
        },
        defaultPublicationId: {
            type: Number,
            default: null,
        },
        publicationLocked: {
            type: Boolean,
            default: false,
        },
        hideUnitId: {
            type: Boolean,
            default: false,
        },
        hidePublicationId: {
            type: Boolean,
            default: false,
        },
        defaultUnitId: {
            type: Number,
            default: null,
        },
        unitLocked: {
            type: Boolean,
            default: false,
        },
        defaultWeekId: {
            type: Number,
            default: null,
        },
        weekLocked: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        form: {
            publication_id: null,
            unit_id: '',
            week_id: '',
            article_id: '',
            related_media_id: '',
            related_media_ids: '',
            media_type: null,
            global: false,
            comment: '',
        },
    }),
    computed: {
        searchablePublicationOptions() {
            return this.publicationOptions.filter(option => option.value !== null)
        },
        publicationPlaceholder() {
            const emptyOption = this.publicationOptions.find(option => option.value === null)

            return emptyOption ? emptyOption.text : 'Enter a publication SKU'
        },
        selectedPublication: {
            get() {
                return this.searchablePublicationOptions.find(option => option.value === this.form.publication_id) || null
            },
            set(publication) {
                this.form.publication_id = publication ? publication.value : null
            },
        },
        visibleFields() {
            if (this.reportType === 'untitled_weeks') {
                return {
                    publication_id: true,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'untitled_panels') {
                return {
                    publication_id: true,
                    unit_id: true,
                    week_id: true,
                    article_id: true,
                    related_media_id: false,
                    global: true,
                }
            }

            if (
                this.reportType === 'printables_by_sku'
                || this.reportType === 'article_by_sku'
            ) {
                return {
                    publication_id: true,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'weekly_phenomenon_by_unit') {
                return {
                    publication_id: !this.hidePublicationId,
                    unit_id: !this.hideUnitId,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'articles_without_narration') {
                return {
                    publication_id: !this.weekLocked,
                    unit_id: false,
                    week_id: this.weekLocked,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'articles_with_narration') {
                return {
                    publication_id: !this.weekLocked,
                    unit_id: false,
                    week_id: this.weekLocked,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'media_images_missing_alt') {
                return {
                    publication_id: false,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'media_videos_missing_captions') {
                return {
                    publication_id: false,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (
                this.reportType === 'related_media_videos_by_sku'
                || this.reportType === 'related_media_by_sku'
            ) {
                return {
                    publication_id: true,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'related_media_locations_by_id') {
                return {
                    publication_id: false,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: true,
                    global: false,
                }
            }

            if (this.reportType === 'related_media_by_article_id') {
                return {
                    publication_id: false,
                    unit_id: false,
                    week_id: false,
                    article_id: true,
                    related_media_id: false,
                    global: false,
                }
            }

            if (this.reportType === 'batch_related_media_pedigree') {
                return {
                    publication_id: false,
                    unit_id: false,
                    week_id: false,
                    article_id: false,
                    related_media_id: false,
                    related_media_ids: true,
                    global: false,
                }
            }

            return {
                publication_id: false,
                unit_id: false,
                week_id: false,
                article_id: false,
                related_media_id: false,
                global: false,
            }
        },
        showGlobalNotice() {
            return this.reportType === 'untitled_panels'
        },
        showUnitPrecedenceNotice() {
            return this.reportType === 'weekly_phenomenon_by_unit'
                && !this.hideUnitId
                && !this.hidePublicationId
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(isVisible) {
                if (isVisible) {
                    this.resetForm()
                }
            },
        },
        reportType() {
            if (this.value) {
                this.resetForm()
            }
        },
        defaultPublicationId() {
            if (this.value) {
                this.form.publication_id = this.defaultPublicationId
            }
        },
        defaultUnitId() {
            if (this.value) {
                this.form.unit_id = this.defaultUnitId
            }
        },
        defaultWeekId() {
            if (this.value) {
                this.form.week_id = this.defaultWeekId
            }
        },
    },
    methods: {
        resetForm() {
            this.form = {
                publication_id: this.defaultPublicationId,
                unit_id: this.defaultUnitId || '',
                week_id: this.defaultWeekId || '',
                article_id: '',
                related_media_id: '',
                related_media_ids: '',
                media_type: null,
                global: false,
                comment: '',
            }
        },
        hide() {
            this.$emit('hide')
        },
        hidden() {
            this.resetForm()
            this.$emit('hide')
        },
        async submit() {
            try {
                const related_media_ids = this.reportType === 'batch_related_media_pedigree'
                    ? await this.processRelatedMediaIds(this.form.related_media_ids)
                    : null
                this.$emit('submit', {
                    report_type: this.reportType,
                    publication_id: this.form.publication_id,
                    unit_id: this.form.unit_id,
                    week_id: this.form.week_id,
                    article_id: this.form.article_id,
                    related_media_id: this.form.related_media_id,
                    related_media_ids: related_media_ids,
                    media_type: this.reportType === 'related_media_videos_by_sku' ? this.form.media_type : null,
                    global: this.form.global,
                    comment: this.form.comment,
                })
            } catch (e) {
                // suppress as we already popped an alert prior
            }
        },
        async processRelatedMediaIds(related_media_ids) {
            const trimmedIds = related_media_ids.trim()
            if (trimmedIds === '') {
                await this.$swal({
                    html: 'At least one media ID is required',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#E74C3C',
                })
                throw new Error('No media IDs found.')
            }
            // split on newlines and trim on the split substrings
            const processedMediaIds = trimmedIds.split('\n').map((row) => row.trim())
            const invalidMediaIds = processedMediaIds.filter((row) => !Number(row))
            if (invalidMediaIds.length === 0) {
                return processedMediaIds
            }

            const html = `
                <p>Invalid IDs found, please fix the following IDs and try again.</p>
                <table class="table table-striped">
                    <thead><th>ID</th></thead>
                    <tbody>
                        ${invalidMediaIds.map((row) => `<tr><td>${row}</td></tr>`)}
                    </tbody>
                </table>
            `
            await this.$swal({
                html,
                confirmButtonText: 'OK',
                confirmButtonColor: '#E74C3C',
            })
            throw new Error('Invalid media IDs found.')
        }
    },
}
</script>
